// Validation, request bodies and the save sequence for the Team member dialog
// (sign-in optional, employment type, salary).

import { isLoginEnabled, employmentTypeOf } from '../../pages/team/teamMembers';

export const ADMIN_VALUE = 'TC_ADMIN';

const num = (v) => (v === '' || v === null || v === undefined ? null : Math.round(Number(v) * 100) / 100);

// A TC_STAFF without a custom role (e.g. a person added without sign-in) has no role yet — never default to Admin.
export const roleValueOf = (u) => {
  if (!u) return '';
  if (u.role === 'TC_STAFF') {
    const roleId = u.role_id || u.roleId;
    return roleId ? `ROLE:${roleId}` : '';
  }
  return ADMIN_VALUE;
};

// salaryMode 'parts' = basic + allowances; 'single' = one monthly amount. Older forms without a mode use parts when filled.
const usesParts = (form) => (form.salaryMode
  ? form.salaryMode === 'parts'
  : form.basic !== '' || form.allowances !== '');

const salaryTotal = (form) => (usesParts(form)
  ? (form.basic === '' && form.allowances === '' ? null : (num(form.basic) || 0) + (num(form.allowances) || 0))
  : num(form.salary));

// thisMonth: 'YYYY-MM'. "Salary from" always starts at this month so editing the amount never rewrites earlier
// months; the person may still pick an earlier month on purpose.
// currentPartnerId: the partner row this member is already linked to as an owner (or null).
// bank: the person's bank details record (or null).
export const initialForm = (user, profile, thisMonth, currentPartnerId = null, bank = null) => {
  const current = profile?.currentSalary || null;
  const parts = current?.basic != null || current?.allowances != null;
  return {
    fullName: user?.full_name || user?.fullName || '',
    loginEnabled: user ? isLoginEnabled(user) : true,
    username: user && isLoginEnabled(user) ? (user.username || '') : '',
    email: user?.email || '',
    password: '',
    roleValue: roleValueOf(user),
    active: user ? user.active !== false : true,
    employmentType: currentPartnerId ? 'OWNER' : (user ? employmentTypeOf(user, profile) : 'EMPLOYEE'),
    joinDate: profile?.joinDate || '',
    salaryMode: parts ? 'parts' : 'single',
    salary: current?.total != null ? String(current.total) : '',
    salaryFrom: thisMonth,
    basic: current?.basic != null ? String(current.basic) : '',
    allowances: current?.allowances != null ? String(current.allowances) : '',
    ownerChoice: currentPartnerId || '',
    alsoSalary: Boolean(current),
    bankName: bank?.bankName || '',
    iban: bank?.iban || '',
    accountNumber: bank?.accountNumber || '',
  };
};

export const normalizeIban = (v) => String(v || '').replace(/\s+/g, '').toUpperCase();
const IBAN_RE = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/;

// Choosing Owner: owners usually need full access, so sign-in goes on with Admin unless a role was already picked.
export const withType = (form, type) => (type === 'OWNER'
  ? { ...form, employmentType: type, loginEnabled: true, roleValue: form.roleValue || ADMIN_VALUE }
  : { ...form, employmentType: type });

// An owner picked from Partners takes the partner's name; the name filled that way is cleared again when the
// choice moves to "Set up owner", where the person types it.
export const withOwnerChoice = (form, choice, partners = []) => {
  const picked = partners.find((p) => p.id === choice);
  const previous = partners.find((p) => p.id === form.ownerChoice);
  if (picked) return { ...form, ownerChoice: choice, fullName: picked.name };
  const autoFilled = previous && form.fullName === previous.name;
  return { ...form, ownerChoice: choice, fullName: autoFilled ? '' : form.fullName };
};

// Who may be paid a salary: employees; owners only at a company and only when "Also paid a salary" is on.
export const salaryAllowed = (form, businessType) => form.employmentType === 'EMPLOYEE'
  || (form.employmentType === 'OWNER' && businessType === 'COMPANY' && Boolean(form.alsoSalary));

// { isEdit, hadLogin } — hadLogin: the member could already sign in before this edit.
export const validateMember = (form, { isEdit, hadLogin }) => {
  if (form.employmentType === 'OWNER' && !form.ownerChoice) return 'Choose which partner this owner is, or set up the owner.';
  if (!form.fullName.trim()) return 'Name is required.';
  if (form.loginEnabled) {
    if (!form.username.trim() || !form.email.trim()) return 'Username and email are required to sign in.';
    if (/\s/.test(form.username.trim())) return 'Username cannot contain spaces.';
    if ((!isEdit || !hadLogin) && !form.password) return 'A password is required to sign in.';
    if (!form.roleValue) return 'Choose a role.';
  }
  const salary = salaryTotal(form);
  if (salary !== null && !(salary > 0)) return 'Salary must be more than 0.';
  if (form.iban && !IBAN_RE.test(normalizeIban(form.iban))) return 'IBAN looks wrong — e.g. AE07 0331 2345 6789 0123 456.';
  return null;
};

// Body for POST / PUT /api/users (camelCase legacy API, role_id as today).
export const memberPayload = (form, { isEdit, hadLogin }) => {
  const payload = { fullName: form.fullName.trim(), loginEnabled: form.loginEnabled, active: form.active };
  if (!form.loginEnabled) return { ...payload, role: 'TC_STAFF', role_id: null };
  payload.username = form.username.trim().toLowerCase();
  payload.email = form.email.trim();
  if (!isEdit || !hadLogin) payload.password = form.password;
  if (form.roleValue === ADMIN_VALUE) {
    payload.role = 'TC_ADMIN';
    payload.role_id = null;
  } else {
    payload.role = 'TC_STAFF';
    payload.role_id = form.roleValue.replace('ROLE:', '');
  }
  return payload;
};

// { profile, salary, bank } for the staff profile, its salary and the person's bank details. An Owner is not a stored
// type: the profile is saved (as EMPLOYEE) only for an owner also paid a salary at a company, otherwise there is none.
// salary is null when nothing changed or the type has no salary; bank is null when the details are unchanged.
export const staffChanges = (form, currentSalary, businessType = 'SOLE_ESTABLISHMENT', currentBank = null) => {
  const stored = form.employmentType === 'OWNER' ? (salaryAllowed(form, businessType) ? 'EMPLOYEE' : null) : form.employmentType;
  const profile = stored ? { employmentType: stored, joinDate: form.joinDate || null, active: form.active } : null;
  const details = { bankName: (form.bankName || '').trim(), iban: normalizeIban(form.iban), accountNumber: (form.accountNumber || '').trim() };
  const changed = ['bankName', 'iban', 'accountNumber'].some((k) => details[k] !== (currentBank?.[k] || ''));
  const bank = changed ? { accountHolderName: currentBank?.accountHolderName || form.fullName.trim(), ...details } : null;
  if (!salaryAllowed(form, businessType)) return { profile, salary: null, bank };
  const parts = usesParts(form);
  const basic = parts ? num(form.basic) : null;
  const allowances = parts ? num(form.allowances) : null;
  const total = salaryTotal(form);
  if (total === null) return { profile, salary: null, bank };
  const effectiveFrom = `${form.salaryFrom}-01`;
  const unchanged = currentSalary
    && Number(currentSalary.total) === total
    && (currentSalary.basic ?? null) === basic
    && (currentSalary.allowances ?? null) === allowances;
  return { profile, salary: unchanged ? null : { effectiveFrom, total, basic, allowances }, bank };
};

const messageOf = (e, fallback) => e?.response?.data?.error || e?.response?.data?.message || e?.message || fallback;

// User first, then staff profile and salary, then commission profile. Once the user exists, a later failure returns
// that user so the dialog can retry as an update (never a second person) and says the member was saved.
export const saveMember = async ({
  api, staffService, commissionService, partnerPeopleService, editingUser, typingCenterId, payload,
  staffChanges: staff, owner, commission, recipient, canEditSalary,
}) => {
  let savedUser;
  try {
    const res = editingUser
      ? await api.updateTeamMember(editingUser.id, payload)
      : await api.createTeamMember({ ...payload, typingCenterId });
    savedUser = { ...(editingUser || {}), ...payload, ...(res?.data?.data || {}) };
  } catch (e) {
    return { savedUser: null, error: messageOf(e, 'Could not save the member.') };
  }
  try {
    // The partner link first, then the staff profile (an owner has none unless also paid a salary).
    if (owner?.choice) {
      if (owner.choice === 'SETUP') await partnerPeopleService.setupOwner(savedUser.id);
      else if (owner.choice !== owner.currentPartnerId) await partnerPeopleService.link(owner.choice, savedUser.id);
    }
    if (staff?.profile) {
      const profile = await staffService.saveProfile(savedUser.id, staff.profile);
      if (staff.salary && canEditSalary) await staffService.setSalary(profile.id, staff.salary);
    }
    if (staff?.bank && canEditSalary) await staffService.saveBank(savedUser.id, staff.bank);
    if (commission && recipient) await commissionService.updateRecipient(recipient.id, commission);
    else if (commission && commission.active) await commissionService.createRecipient({ ...commission, userId: savedUser.id });
  } catch (e) {
    return { savedUser, error: `Member saved — ${messageOf(e, 'the remaining details could not be saved')}` };
  }
  return { savedUser, error: null };
};
